import { StatusChip } from '../../components/ui';
import { formatClockTime, shopWallClockDate } from '../../utils/shopTime';
import { useShopTimezones } from '../../hooks/useShopTimezones';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  styled,
  useTheme,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { appointmentsService, mechanicsService, shopsService, tasksService } from '../../services/api';
import { Appointment, AppointmentStatus, Task, UpdateAppointmentDto } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { format, parseISO } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import safeStorage from '../../utils/safeStorage';

const AppointmentCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    boxShadow: theme.shadows[2],
  },
}));

// Helper functions for parsing appointment date and time


// Define the TabValue type
type TabValue = 0 | 1 | 2;

// Tab panel component for different views
interface TabPanelProps {
  children?: React.ReactNode;
  index: TabValue;
  value: TabValue;
}

// Update the TabPanel to use theme colors
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  const theme = useTheme();

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`schedule-tabpanel-${index}`}
      aria-labelledby={`schedule-tab-${index}`}
      {...other}
      style={{ 
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(1.5), // Reduced padding
        marginTop: theme.spacing(1.5) // Reduced margin
      }}
    >
      {value === index && children}
    </div>
  );
};

function a11yProps(index: TabValue) {
  return {
    id: `schedule-tab-${index}`,
    'aria-controls': `schedule-tabpanel-${index}`,
  };
}

// Enhanced Appointment type for local use
interface AppointmentWithDate extends Appointment {
  parsedDate?: Date;
  parsedTime?: Date;
}

// Helper to derive a date key from an appointment — on the shop's calendar
// (see shopWallClockDate), so an afternoon job in Texas stays on its own day
// for a viewer in any timezone.
const keyFromDate = (timestamp: number, timeZone?: string | null): string => {
  const date = shopWallClockDate(timestamp, timeZone);
  return [
    date.getFullYear(),
    (date.getMonth() + 1).toString().padStart(2, '0'),
    date.getDate().toString().padStart(2, '0'),
  ].join('-');
};

const getAppointmentDateKey = (
  appointment: AppointmentWithDate,
  timeZone?: string | null,
): string | null => {
  if (appointment.appointmentDateTime) {
    return keyFromDate(appointment.appointmentDateTime, timeZone);
  }

  if (appointment.appointmentDate) {
    return keyFromDate(appointment.appointmentDate, timeZone);
  }

  if (appointment.appointmentDateObj instanceof Date && !isNaN(appointment.appointmentDateObj.getTime())) {
    const timestamp = Math.floor(appointment.appointmentDateObj.getTime() / 1000);
    return keyFromDate(timestamp);
  }

  return null;
};

const formatAppointmentStatusLabel = (status: AppointmentStatus) =>
  status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const mechanicEditableStatuses = [
  AppointmentStatus.CONFIRMED,
  AppointmentStatus.IN_PROGRESS,
  AppointmentStatus.COMPLETED,
  AppointmentStatus.CANCELLED,
];

const StatusBadge: React.FC<{ status: AppointmentStatus }> = ({ status }) => (
  <StatusChip status={status} sx={{ height: 22 }} />
);

// Format date for display - moved outside component
const formatDateString = (dateString: string) => {
  try {
    const date = parseISO(`${dateString}T00:00:00`);
    return format(date, 'EEEE, MMMM d, yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

// Format time for display - moved outside component
const formatTimeString = formatClockTime;

const getCurrentWeekDays = (inputDate: Date) => {
  // Get the day of the week (0-6, 0 being Sunday)
  const dayOfWeek = inputDate.getDay();
  
  // Calculate the date of the previous Sunday
  const sunday = new Date(inputDate);
  sunday.setDate(inputDate.getDate() - dayOfWeek);
  
  // Generate array of dates from Sunday to Saturday
  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(sunday);
    day.setDate(sunday.getDate() + i);
    weekDays.push(day);
  }
  
  return weekDays;
};

// Add this function - it is referenced in the code but not defined
const parseAppointmentDates = (appointments: Appointment[]): AppointmentWithDate[] => {
  return appointments.map(appointment => {
    try {
      // Create a safe copy of the appointment
      const appointmentWithDate: AppointmentWithDate = { ...appointment };
      
      // Safely parse the date
      try {
        if (appointment.appointmentDate) {
          // appointmentDate is now an epoch timestamp
          let parsedDate: Date | undefined;
          try {
            parsedDate = new Date(appointment.appointmentDate * 1000);
            // Keep the epoch timestamp as is
            appointmentWithDate.appointmentDate = appointment.appointmentDate;
          } catch (error) {
            console.error('Error parsing appointment date:', error);
          }
          
          // Store the parsed date object for easy comparison only if valid
          if (parsedDate && !isNaN(parsedDate.getTime())) {
            appointmentWithDate.parsedDate = parsedDate;
          }
        }
      } catch (error) {
        console.error('Error parsing appointment date:', error, appointment.appointmentDate);
      }
      
      // Safely parse the time
      try {
        if (appointment.appointmentTime) {
          // Ensure time is in HH:mm:ss format
          let parsedTime: Date | undefined;
          try {
            parsedTime = parseISO(`2000-01-01T${appointment.appointmentTime}`);
            // Update time format if needed
            appointmentWithDate.appointmentTime = format(parsedTime, 'HH:mm:ss');
          } catch (error) {
            // Try alternative time parsing
            const timeParts = appointment.appointmentTime.split(':');
            if (timeParts.length >= 2) {
              const timeString = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}:00`;
              appointmentWithDate.appointmentTime = timeString;
              parsedTime = parseISO(`2000-01-01T${timeString}`);
            }
          }
          
          // Store the parsed time object only if we successfully parsed it
          if (parsedTime && !isNaN(parsedTime.getTime())) {
            appointmentWithDate.parsedTime = parsedTime;
          }
        }
      } catch (error) {
        console.error('Error parsing appointment time:', error, appointment.appointmentTime);
      }
      
      return appointmentWithDate;
    } catch (error) {
      console.error('Error processing appointment:', error);
      // Return the original appointment if parsing fails
      return appointment;
    }
  });
};

const SchedulePage: React.FC = () => {
  const [appointments, setAppointments] = useState<AppointmentWithDate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState<TabValue>(0);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mechanicId, setMechanicId] = useState<string | null>(null);
  const [showAllTimeSlots, setShowAllTimeSlots] = useState(() => {
    // Initialize from storage with fallback to false
    try {
      const savedValue = safeStorage.getItem('showAllTimeSlots');
      return savedValue === 'true';
    } catch (e) {
      return false;
    }
  });
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [mechanicData, setMechanicData] = useState<any>(null);

  // The mechanic's shop zone: days and "today" follow the shop's calendar.
  const tzOf = useShopTimezones([mechanicData?.shopId]);
  const shopTz = tzOf(mechanicData?.shopId);
  useEffect(() => {
    if (shopTz) setCurrentDate(shopWallClockDate(Math.floor(Date.now() / 1000), shopTz));
  }, [shopTz]);
  const [shopSettings, setShopSettings] = useState<any>(null);
  
  // Appointment details dialog state
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentWithDate | null>(null);
  const [appointmentTasks, setAppointmentTasks] = useState<Task[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const { user } = useAuth();
  const { appointments: socketAppointments, connected: socketConnected } = useSocket();

  // Get theme from the global context provider
  const theme = useTheme();

  // Memoize handlers to prevent unnecessary renders
  const handleDateChange = useCallback((date: Date | null) => {
    if (date) {
      setCurrentDate(date);
    }
  }, []);

  const handlePreviousDay = () => {
    setCurrentDate(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() - 1);
      return next;
    });
    setTabValue(0);
  };

  const handleNextDay = () => {
    setCurrentDate(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() + 1);
      return next;
    });
    setTabValue(0);
  };

  const handlePreviousWeek = () => {
    setCurrentDate(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() - 7);
      return next;
    });
    setTabValue(1);
  };

  const handleNextWeek = () => {
    setCurrentDate(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() + 7);
      return next;
    });
    setTabValue(1);
  };

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: TabValue) => {
    setTabValue(newValue);
  }, []);

  const toggleTimeSlots = useCallback(() => {
    setShowAllTimeSlots(prev => {
      const newValue = !prev;
      try {
        safeStorage.setItem('showAllTimeSlots', newValue.toString());
      } catch (e) {
        console.warn('Error saving time slot preference to storage:', e);
      }
      return newValue;
    });
  }, []);

  const handleToday = useCallback(() => {
    setCurrentDate(shopWallClockDate(Math.floor(Date.now() / 1000), shopTz));
  }, [shopTz]);

  const handleAppointmentClick = async (appointment: AppointmentWithDate) => {
    setSelectedAppointment(appointment);
    try {
      const tasks = await tasksService.getTasksByAppointmentId(appointment.id);
      setAppointmentTasks(tasks);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      setAppointmentTasks([]);
    }
    setDialogOpen(true);
  };

  const handleMechanicStatusChange = async (newStatus: AppointmentStatus) => {
    if (!selectedAppointment) return;
    setStatusUpdating(true);
    setError(null);
    try {
      const updateData: UpdateAppointmentDto = { status: newStatus };
      const updatedAppointment = await appointmentsService.updateAppointment(selectedAppointment.id, updateData);

      setSelectedAppointment(prev =>
        prev && prev.id === updatedAppointment.id ? { ...prev, status: updatedAppointment.status } : prev,
      );

      setAppointments(prev =>
        prev.map(app =>
          app.id === updatedAppointment.id ? { ...app, status: updatedAppointment.status } : app,
        ),
      );

      setInfoMessage(`Status moved to ${formatAppointmentStatusLabel(updatedAppointment.status)}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleMarkCompleted = async (appointment: AppointmentWithDate) => {
    setIsMarkingComplete(true);
    try {
      setError(null);
      const updatedAppointment = await appointmentsService.markAppointmentCompleted(appointment.id);
      setAppointments(prev =>
        prev.map(app => (app.id === appointment.id ? { ...app, status: updatedAppointment.status } : app)),
      );
      setSelectedAppointment(prev =>
        prev && prev.id === appointment.id ? { ...prev, status: updatedAppointment.status } : prev,
      );
      const refreshedTasks = await tasksService.getTasksByAppointmentId(appointment.id);
      setAppointmentTasks(refreshedTasks);
      setInfoMessage('Job marked Completed and tasks marked as Finished.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to mark job as complete');
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedAppointment(null);
    setAppointmentTasks([]);
  };

  const canMarkCompletedStatus = (status: AppointmentStatus) =>
    [AppointmentStatus.IN_PROGRESS, AppointmentStatus.CONFIRMED].includes(status);

  useEffect(() => {
    if (user?.id) {
      const fetchData = async () => {
        setLoading(true);
        
        // Set a timeout to ensure we don't get stuck in loading state
        const loadingTimeout = setTimeout(() => {
          setLoading(false);
          setError('Loading timed out. Please try refreshing the page.');
        }, 15000); // 15 seconds timeout
        
        try {
          // Fetch mechanic data
          const mechData = await mechanicsService.getMechanicByUserId(user.id);
          setMechanicData(mechData);
          
          if (!mechData || !mechData.id) {
            console.error('No mechanic data found for user:', user.id);
            setError('Could not find mechanic data for current user');
            setLoading(false);
            clearTimeout(loadingTimeout);
            return;
          }
          
          // Fetch shop settings for business hours and time slot configuration
          try {
            const shopSettingsData = await shopsService.getShopSettings(mechData.shopId);
            setShopSettings(shopSettingsData);
          } catch (settingsError) {
            console.error('Error fetching shop settings:', settingsError);
            // Use default settings if shop settings can't be fetched
            setShopSettings({
              timeSlotSize: 45,
              businessHours: {
                MONDAY: { open: '09:00', close: '18:00' },
                TUESDAY: { open: '09:00', close: '18:00' },
                WEDNESDAY: { open: '09:00', close: '18:00' },
                THURSDAY: { open: '09:00', close: '18:00' },
                FRIDAY: { open: '09:00', close: '18:00' },
                SATURDAY: { open: '10:00', close: '15:00' },
                SUNDAY: { open: null, close: null }
              }
            });
          }
          
          // Fetch appointments for the mechanic
          try {
            const fetchedAppointments = await appointmentsService.getMechanicAppointments(mechData.id);
            const parsedAppointments = parseAppointmentDates(fetchedAppointments);
            setAppointments(parsedAppointments);
            setError(null);
          } catch (appointmentError) {
            console.error('Error fetching appointments:', appointmentError);
            setError('Failed to load appointments');
            setAppointments([]);
          }
        } catch (error) {
          console.error('Error fetching mechanic data:', error);
          setError('Failed to load mechanic data');
        } finally {
          clearTimeout(loadingTimeout);
          setLoading(false);
        }
      };
      
      fetchData().catch(err => {
        console.error('Unhandled error in fetchData:', err);
        setError('An unexpected error occurred');
        setLoading(false);
      });
    } else {
      // If there's no user ID, we should not be in a loading state
      setLoading(false);
      setError('User authentication required');
    }
  }, [user?.id]);

  // Add debug log for mechanicData changes
  // Save/retrieve selected tab from localStorage with proper error handling
  useEffect(() => {
    try {
      const savedTab = safeStorage.getItem('selectedTab');
      if (savedTab !== null) {
        const parsedValue = parseInt(savedTab);
        // Ensure the parsed value is one of the valid TabValues (0, 1, 2)
        if (!isNaN(parsedValue) && [0, 1, 2].includes(parsedValue)) {
          setTabValue(parsedValue as TabValue);
        } else {
          // If parsing fails or value is out of range, default to daily view
          setTabValue(0);
        }
      }
    } catch (error) {
      console.warn('Error retrieving selected tab from storage:', error);
      // Default to daily view (0) if there's an error
      setTabValue(0);
    }
  }, []);

  useEffect(() => {
    try {
      safeStorage.setItem('selectedTab', tabValue.toString());
    } catch (error) {
      console.warn('Error saving selected tab to storage:', error);
    }
  }, [tabValue]);

  // Save showAllTimeSlots to storage when it changes
  useEffect(() => {
    try {
      safeStorage.setItem('showAllTimeSlots', showAllTimeSlots.toString());
    } catch (error) {
      console.warn('Error saving time slot preference to storage:', error);
    }
  }, [showAllTimeSlots]);

  // Update appointments when socket updates occur - with safety checks
  useEffect(() => {
    if (socketConnected && socketAppointments && socketAppointments.length > 0 && mechanicData?.id) {
      // Filter appointments for this mechanic only
      const relevantAppointments = socketAppointments.filter(
        app => app.mechanicId === mechanicData.id
      );
      
      if (relevantAppointments.length > 0) {
        setAppointments(prev => {
          // Create a map of existing appointments for quicker lookup
          const existingAppointmentsMap = new Map(prev.map(a => [a.id, a]));
          
          // Process only new appointments for this mechanic
          const newAppointments = relevantAppointments.filter(a => !existingAppointmentsMap.has(a.id));
          
          // If there are no new appointments, return the previous state
          if (newAppointments.length === 0) return prev;
          
          // Otherwise, add the new appointments
          return [...prev, ...newAppointments];
        });
      }
    }
  }, [socketAppointments, socketConnected, mechanicData]);

  // Memoize the sorted data to prevent recalculation on each render
  const { appointmentsByDate, sortedDates } = useMemo(() => {
    const byDate = appointments.reduce<Record<string, AppointmentWithDate[]>>((groups, appointment) => {
      const dateKey = getAppointmentDateKey(appointment, shopTz);
      if (!dateKey) {
        console.warn('Appointment missing date:', appointment.id);
        return groups;
      }

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }

      groups[dateKey].push(appointment);
      return groups;
    }, {});

    // Sort dates - improved validation to handle more date formats
    const sorted = Object.keys(byDate)
      .sort((a, b) => {
        // Sort by timestamp if possible, otherwise by string
        try {
          const dateA = new Date(a).getTime();
          const dateB = new Date(b).getTime();
          return dateA - dateB;
        } catch (error) {
          return a.localeCompare(b);
        }
      });
      
    return { appointmentsByDate: byDate, sortedDates: sorted };
  }, [appointments, shopTz]);

  // Define getAppointmentsForDay function before it's used in useMemo hooks
  const getAppointmentsForDay = useCallback((date: Date) => {
    const targetDateString = format(date, 'yyyy-MM-dd');

    return appointments.filter(appointment => {
      const appointmentDateKey = getAppointmentDateKey(appointment, shopTz);
      return appointmentDateKey === targetDateString;
    });
  }, [appointments, shopTz]);

  // Memoized filtered appointments for daily view
  const filteredDailyAppointments = useMemo(() => {
    return getAppointmentsForDay(currentDate);
  }, [getAppointmentsForDay, currentDate]);

  // Weekly days calculation moved to useMemo for performance
  const weekDays = useMemo(() => {
    return getCurrentWeekDays(currentDate);
  }, [currentDate]);

    // Memoized filtered appointments for weekly view
  const filteredWeeklyAppointments = useMemo(() => {
    // Early return if appointments haven't loaded yet
    if (loading || appointments.length === 0) {
      return weekDays.map(day => ({
        date: day,
        appointments: []
      }));
    }
    
    const result = weekDays.map(day => {
      const dayAppointments = getAppointmentsForDay(day);
      return {
        date: day,
        appointments: dayAppointments
      };
    });
    return result;
  }, [weekDays, getAppointmentsForDay, appointments, loading]);

  // Get the time range to display based on actual appointments
  const getActiveTimeRange = (appointmentsForDay: AppointmentWithDate[]) => {
    // Get business hours from shop settings
    const getBusinessHours = () => {
      if (!shopSettings?.businessHours) {
        return { startHour: 9, endHour: 18 }; // Default fallback
      }
      
      // For now, use Monday's hours as default (could be enhanced to use current day)
      const mondayHours = shopSettings.businessHours.MONDAY;
      if (!mondayHours || !mondayHours.open || !mondayHours.close) {
        return { startHour: 9, endHour: 18 }; // Default if Monday is closed or invalid
      }
      
      const startHour = parseInt(mondayHours.open.split(':')[0], 10);
      const endHour = parseInt(mondayHours.close.split(':')[0], 10);
      return { startHour, endHour };
    };
    
    const { startHour: businessStartHour, endHour: businessEndHour } = getBusinessHours();
    
    if (!appointmentsForDay || appointmentsForDay.length === 0) {
      // Use business hours if no appointments
      return {
        start: businessStartHour,
        end: businessEndHour,
        slots: generateTimeSlots(businessStartHour, businessEndHour)
      };
    }

    // Extract hours from appointment times
    const hours = appointmentsForDay.map(appointment => {
      const timeString = appointment.appointmentTime;
      const hour = parseInt(timeString.substring(0, 2), 10);
      return hour;
    });

    // Find earliest and latest hour with buffer, but respect business hours
    const earliestHour = Math.max(businessStartHour, Math.min(...hours) - 1);
    const latestHour = Math.min(businessEndHour, Math.max(...hours) + 2);
    
    // Generate time slots for this range
    const slots = generateTimeSlots(earliestHour, latestHour);
    
    return {
      start: earliestHour,
      end: latestHour,
      slots
    };
  };

  // Generate time slots for day view with configurable range
  const generateTimeSlots = (startHour = 9, endHour = 18) => {
    const slots = [];
    // Get the time slot size from shop settings
    const timeSlotSizeMinutes = shopSettings?.timeSlotSize || 45;
    
    // Create slots from startHour to endHour based on the time slot size
    let currentTime = startHour * 60; // Convert to minutes, starting from shop opening time
    const endTime = endHour * 60;
    
    while (currentTime < endTime) {
      const hours = Math.floor(currentTime / 60);
      const minutes = currentTime % 60;
      const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
      slots.push(timeString);
      currentTime += timeSlotSizeMinutes;
    }
    
    return slots;
  };

  const renderAppointmentDetails = useCallback(
    (
      appointment: AppointmentWithDate,
      options?: { showTime?: boolean; showContact?: boolean; compact?: boolean }
    ): React.ReactNode => {
      const { showTime = false, showContact = true, compact = false } = options ?? {};
      const spacing = compact ? 0.75 : 1.25;
      const headingVariant: 'body2' | 'subtitle1' = compact ? 'body2' : 'subtitle1';

      const timeLabel = appointment.appointmentTime
        ? formatTimeString(appointment.appointmentTime)
        : 'Time not set';

      const vehicleSummary = [
        appointment.vehicleModel,
        appointment.vehicleYear ? `(${appointment.vehicleYear})` : null
      ]
        .filter(Boolean)
        .join(' ');

      const serviceSummary = [vehicleSummary, appointment.serviceDescription]
        .filter(Boolean)
        .join(' | ');

      const contactSummary = [appointment.customerEmail, appointment.customerPhone]
        .filter(Boolean)
        .join(' | ');

      return (
        <Stack spacing={spacing}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Typography variant={headingVariant} fontWeight={600}>
              {appointment.customerName || 'Customer'}
            </Typography>
            <StatusBadge status={appointment.status} />
          </Stack>
          {showTime && (
            <Typography variant="body2" color="text.secondary">
              {timeLabel}
            </Typography>
          )}
          {serviceSummary && (
            <Typography variant="body2" color="text.secondary">
              {serviceSummary}
            </Typography>
          )}
          {showContact && contactSummary && (
            <Typography variant="caption" color="text.secondary">
              {contactSummary}
            </Typography>
          )}
        </Stack>
      );
    },
    []
  );

  const renderListView = (): React.ReactNode => {
    if (appointments.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          You have no appointments scheduled. Check back later.
        </Typography>
      );
    }

    if (sortedDates.length === 0) {
      return (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            You have appointments but they could not be grouped by date. Showing all appointments below.
          </Typography>
          <Stack spacing={1.5}>
            {appointments.map((appointment) => (
              <AppointmentCard key={appointment.id} onClick={() => handleAppointmentClick(appointment)}>
                {renderAppointmentDetails(appointment, { showTime: true })}
              </AppointmentCard>
            ))}
          </Stack>
        </Stack>
      );
    }

    return (
      <Stack spacing={3}>
        {sortedDates.map((date) => (
          <Stack key={date} spacing={1.25}>
            <Typography variant="subtitle1" fontWeight={600}>
              {formatDateString(date)}
            </Typography>
            <Stack spacing={1.5}>
              {appointmentsByDate[date]
                .sort((a, b) => a.appointmentTime.localeCompare(b.appointmentTime))
                .map((appointment) => (
                  <AppointmentCard key={appointment.id} onClick={() => handleAppointmentClick(appointment)}>
                    {renderAppointmentDetails(appointment, { showTime: true })}
                  </AppointmentCard>
                ))}
            </Stack>
          </Stack>
        ))}
      </Stack>
    );
  };

  // Helper function to extract time string (HH:mm) from an appointment
  const getAppointmentTimeString = useCallback((appointment: AppointmentWithDate): string | null => {
    // First try appointmentTime if it exists
    if (appointment.appointmentTime) {
      const timeStr = appointment.appointmentTime.substring(0, 5); // Get HH:mm
      return timeStr;
    }
    
    // If appointmentDateTime exists, extract time from it
    if (appointment.appointmentDateTime) {
      try {
        // appointmentDateTime is now an epoch timestamp
        // appointmentTime (shop-local) is preferred above; this fallback
        // reads the epoch in the browser zone and is only hit by legacy rows.
        const dateTime = new Date(appointment.appointmentDateTime * 1000);
        return format(dateTime, 'HH:mm');
      } catch (error) {
        console.error('Error parsing appointment time:', error);
        return null;
      }
    }
    
    return null;
  }, []);

  const parseTimeToMinutes = (time: string): number => {
    const [hourPart, minutePart] = time.split(':');
    const hours = parseInt(hourPart, 10);
    const minutes = parseInt(minutePart, 10);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return 0;
    }
    return hours * 60 + minutes;
  };

  const doesAppointmentFallInSlot = (
    slotPrefix: string,
    appointment: AppointmentWithDate,
    slotSizeMinutes: number,
  ) => {
    const appointmentTime = getAppointmentTimeString(appointment);
    if (!appointmentTime) return false;
    const appointmentMinutes = parseTimeToMinutes(appointmentTime.substring(0, 5));
    const slotStart = parseTimeToMinutes(slotPrefix);
    return appointmentMinutes >= slotStart && appointmentMinutes < slotStart + slotSizeMinutes;
  };

  const renderDailyView = (): React.ReactNode => {
    const todayAppointments = filteredDailyAppointments;
    const { slots: allTimeSlots } = getActiveTimeRange(todayAppointments);

    const slotSizeMinutes = shopSettings?.timeSlotSize || 45;
    const appointmentTimeSlots = allTimeSlots.filter((timeSlot) => {
      const slotPrefix = timeSlot.substring(0, 5);
      return todayAppointments.some((appointment) =>
        doesAppointmentFallInSlot(slotPrefix, appointment, slotSizeMinutes),
      );
    });

    const displayTimeSlots =
      showAllTimeSlots || todayAppointments.length === 0 ? allTimeSlots : appointmentTimeSlots;

    const dateLabel = format(currentDate, 'EEEE, MMMM d, yyyy');

    return (
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {dateLabel}
          </Typography>
          <Button onClick={toggleTimeSlots} size="small" variant="text">
            {showAllTimeSlots ? 'Show Busy Slots Only' : 'Show Full Day'}
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {todayAppointments.length === 0
            ? 'No appointments scheduled for this day.'
            : `${todayAppointments.length} appointment${todayAppointments.length === 1 ? '' : 's'} scheduled.`}
        </Typography>

        <Paper variant="outlined">
          {displayTimeSlots.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No time slots available for the selected configuration.
            </Typography>
          ) : (
            displayTimeSlots.map((timeSlot, index) => {
              const slotPrefix = timeSlot.substring(0, 5);
              const appointmentsForSlot = todayAppointments.filter((item) =>
                doesAppointmentFallInSlot(slotPrefix, item, slotSizeMinutes),
              );
              const isLast = index === displayTimeSlots.length - 1;

              return (
                <Stack
                  key={timeSlot}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: isLast ? 'none' : `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: { sm: 96 } }}>
                    {formatTimeString(timeSlot)}
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    {appointmentsForSlot.length > 0 ? (
                      <Stack spacing={1.5}>
                        {appointmentsForSlot.map((appointment) => (
                          <Box 
                            key={appointment.id} 
                            onClick={() => handleAppointmentClick(appointment)}
                            sx={{
                              cursor: 'pointer',
                              p: 1,
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            {renderAppointmentDetails(appointment, { showTime: false, compact: true })}
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Available
                      </Typography>
                    )}
                  </Box>
                </Stack>
              );
            })
          )}
        </Paper>
      </Stack>
    );
  };

  const renderWeeklyView = (): React.ReactNode => {
    const weekAppointments: AppointmentWithDate[] = filteredWeeklyAppointments.flatMap(
      (day) => day.appointments
    );

    if (weekAppointments.length === 0) {
      return (
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton onClick={handlePreviousWeek} size="small" color="primary">
                <ChevronLeft />
              </IconButton>
              <Typography variant="subtitle1" fontWeight={600} sx={{ minWidth: 200, textAlign: 'center' }}>
                Week of {format(weekDays[0], 'MMMM d, yyyy')}
              </Typography>
              <IconButton onClick={handleNextWeek} size="small" color="primary">
                <ChevronRight />
              </IconButton>
              <Button onClick={handleToday} size="small" variant="outlined" sx={{ ml: 1 }}>
                Today
              </Button>
            </Stack>
            <Button onClick={toggleTimeSlots} size="small" variant="text">
              {showAllTimeSlots ? 'Show Busy Slots Only' : 'Show Full Day'}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            No appointments scheduled for this week.
          </Typography>
        </Stack>
      );
    }

    const { slots: allTimeSlots } = getActiveTimeRange(weekAppointments);
    const slotSizeMinutes = shopSettings?.timeSlotSize || 45;
    const appointmentTimeSlots = allTimeSlots.filter((timeSlot) => {
      const prefix = timeSlot.substring(0, 5);
      return weekAppointments.some((appointment) =>
        doesAppointmentFallInSlot(prefix, appointment, slotSizeMinutes),
      );
    });

    const displayTimeSlots =
      showAllTimeSlots || appointmentTimeSlots.length === 0 ? allTimeSlots : appointmentTimeSlots;

    return (
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={handlePreviousWeek} size="small" color="primary">
              <ChevronLeft />
            </IconButton>
            <Typography variant="subtitle1" fontWeight={600} sx={{ minWidth: 200, textAlign: 'center' }}>
              Week of {format(weekDays[0], 'MMMM d, yyyy')}
            </Typography>
            <IconButton onClick={handleNextWeek} size="small" color="primary">
              <ChevronRight />
            </IconButton>
            <Button onClick={handleToday} size="small" variant="outlined" sx={{ ml: 1 }}>
              Today
            </Button>
          </Stack>
          <Button onClick={toggleTimeSlots} size="small" variant="text">
            {showAllTimeSlots ? 'Show Busy Slots Only' : 'Show Full Day'}
          </Button>
        </Stack>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 96 }}>Time</TableCell>
                {weekDays.map((day) => (
                  <TableCell key={day.toISOString()}>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" fontWeight={600}>
                        {format(day, 'EEE d')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(day, 'MMM')}
                      </Typography>
                    </Stack>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayTimeSlots.map((timeSlot) => {
                const prefix = timeSlot.substring(0, 5);

                return (
                  <TableRow key={timeSlot}>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatTimeString(timeSlot)}
                      </Typography>
                    </TableCell>
                    {weekDays.map((day, index) => {
                      const dayAppointments = filteredWeeklyAppointments[index]?.appointments ?? [];
                      const appointmentsForSlot = dayAppointments.filter((item) =>
                        doesAppointmentFallInSlot(prefix, item, slotSizeMinutes),
                      );

                      return (
                        <TableCell key={day.toISOString()} sx={{ verticalAlign: 'top' }}>
                          {appointmentsForSlot.length > 0 ? (
                            <Stack spacing={1}>
                              {appointmentsForSlot.map((appointment) => (
                                <Box 
                                  key={appointment.id}
                                  onClick={() => handleAppointmentClick(appointment)}
                                  sx={{
                                    cursor: 'pointer',
                                    p: 1,
                                    borderRadius: 1,
                                    '&:hover': {
                                      backgroundColor: 'action.hover',
                                    },
                                  }}
                                >
                                  {renderAppointmentDetails(appointment, {
                                    showTime: false,
                                    showContact: false,
                                    compact: true,
                                  })}
                                </Box>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Available
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    );
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        {infoMessage && (
          <Alert severity="success" onClose={() => setInfoMessage(null)}>
            {infoMessage}
          </Alert>
        )}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
        >
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {loading
              ? 'Loading Schedule...'
              : mechanicData?.user?.name
                ? `${mechanicData.user.name}'s Schedule`
                : 'Schedule'}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton size="small" onClick={handlePreviousDay}>
              <ChevronLeft />
            </IconButton>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Select Date"
                value={currentDate}
                onChange={handleDateChange}
                slotProps={{ textField: { size: 'small' } }}
              />
            </LocalizationProvider>
            <IconButton size="small" onClick={handleNextDay}>
              <ChevronRight />
            </IconButton>
          </Stack>
        </Stack>

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="schedule view tabs">
              <Tab label="Daily" {...a11yProps(0)} />
              <Tab label="Weekly" {...a11yProps(1)} />
              <Tab label="List" {...a11yProps(2)} />
            </Tabs>
          </Box>
          {tabValue === 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Button size="small" variant="outlined" onClick={handlePreviousWeek}>
                Previous Week
              </Button>
              <Typography variant="subtitle2" color="text.secondary">
                Week of {format(currentDate, 'MMMM d, yyyy')}
              </Typography>
              <Button size="small" variant="outlined" onClick={handleNextWeek}>
                Next Week
              </Button>
            </Box>
          )}

          <TabPanel value={tabValue} index={0}>
            {renderDailyView()}
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {renderWeeklyView()}
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            {renderListView()}
          </TabPanel>
        </Paper>
      </Stack>

      {/* Appointment Details Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Appointment Details
          {selectedAppointment && (
            <StatusChip status={selectedAppointment.status} sx={{ ml: 2 }} />
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedAppointment && (
            <Stack spacing={3}>
              <FormControl fullWidth sx={{ mb: 1 }}>
                <InputLabel id="mechanic-status-label">Status</InputLabel>
                <Select
                  labelId="mechanic-status-label"
                  value={selectedAppointment.status}
                  label="Status"
                  onChange={(event) =>
                    handleMechanicStatusChange(event.target.value as AppointmentStatus)
                  }
                  disabled={statusUpdating}
                >
                  {mechanicEditableStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {formatAppointmentStatusLabel(status)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Customer
                </Typography>
                <Typography variant="body1">
                  {selectedAppointment.customerName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedAppointment.customerEmail} • {selectedAppointment.customerPhone}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Appointment Date & Time
                </Typography>
                <Typography variant="body1">
                  {selectedAppointment.appointmentDateTime || selectedAppointment.appointmentDate
                    ? format(
                        shopWallClockDate(
                          selectedAppointment.appointmentDateTime || selectedAppointment.appointmentDate || 0,
                          shopTz,
                        ),
                        "EEEE, MMMM d, yyyy 'at' h:mm a",
                      )
                    : 'Date not available'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Vehicle
                </Typography>
                <Typography variant="body1">
                  {selectedAppointment.vehicleModel} {selectedAppointment.vehicleYear && `(${selectedAppointment.vehicleYear})`}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Service Description
                </Typography>
                <Typography variant="body1">
                  {selectedAppointment.serviceDescription}
                </Typography>
              </Box>

              {selectedAppointment.comments && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Comments
                  </Typography>
                  <Typography variant="body1">
                    {selectedAppointment.comments}
                  </Typography>
                </Box>
              )}

              {/* Tasks Section */}
              <Box sx={{ mt: 2, pt: 2, borderTop: '2px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  Service Tasks
                </Typography>
                {appointmentTasks.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No tasks found for this appointment.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {appointmentTasks.map((task) => (
                      <Box
                        key={task.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.5,
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {task.title}
                          </Typography>
                          {task.description && (
                            <Typography variant="body2" color="text.secondary">
                              {task.description}
                            </Typography>
                          )}
                          {task.assignedTo && (
                            <Typography variant="body2" color="primary">
                              Assigned to: {task.assignedTo.name}
                            </Typography>
                          )}
                        </Box>
                        <StatusChip status={task.status} />
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {selectedAppointment &&
            canMarkCompletedStatus(selectedAppointment.status) && (
              <Button
                variant="contained"
                color="success"
                onClick={() => handleMarkCompleted(selectedAppointment)}
                disabled={isMarkingComplete}
              >
                {isMarkingComplete ? 'Saving...' : 'Mark Completed'}
              </Button>
            )}
          <Button onClick={handleDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Wrap the component with React.memo for additional performance
export default React.memo(SchedulePage); 